import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { ModalShell } from "@/components/ui/ModalShell";
import { SegmentedControl } from "@/components/ui/SegmentedControl";
import { fieldControlClass, FormField, MessageBanner, ModalActions, TextArea } from "@/components/ui/primitives";
import {
  useCreateSavingInstitution,
  useCreateSavingPocket,
  useSavingInstitutions,
} from "@/hooks/useSavingPockets";
import { Field, FREEZE_PRESET_OPTIONS, FreezeTimeField, SelectField, type FreezePreset } from "./SavingPocketFields";

interface NewSavingPocketModalProps {
  isOpen: boolean;
  onClose: () => void;
}

type Mode = "institution" | "pocket";

const initialInstitution = {
  name: "",
  description: "",
  active: true,
};

const createInitialPocket = () => ({
  saving_institution_id: "",
  name: "",
  amount: "",
  anual_rate: "",
  freeze_time: "",
  starting_date: getTodayInputValue(),
  description: "",
  active: true,
});

export const NewSavingPocketModal = ({ isOpen, onClose }: NewSavingPocketModalProps) => {
  const { t } = useTranslation(["savingPockets", "common"]);
  const [mode, setMode] = useState<Mode>("pocket");
  const [institutionForm, setInstitutionForm] = useState(initialInstitution);
  const [pocketForm, setPocketForm] = useState(createInitialPocket);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [freezePreset, setFreezePreset] = useState<FreezePreset>("none");

  const { data: institutions = [] } = useSavingInstitutions({ enabled: isOpen });
  const createInstitution = useCreateSavingInstitution();
  const createPocket = useCreateSavingPocket();

  useEffect(() => {
    if (!isOpen) {
      setMode("pocket");
      setInstitutionForm(initialInstitution);
      setPocketForm(createInitialPocket());
      setSubmitError(null);
      setFreezePreset("none");
      return;
    }

    if (!pocketForm.saving_institution_id && institutions[0]) {
      setPocketForm((current) => ({ ...current, saving_institution_id: String(institutions[0].id) }));
    }
  }, [institutions, isOpen, pocketForm.saving_institution_id]);

  useEffect(() => {
    setFreezePreset(getFreezePreset(pocketForm.freeze_time));
  }, [pocketForm.freeze_time]);

  const isSubmitting = createInstitution.isPending || createPocket.isPending;

  const institutionOptions = useMemo(
    () => institutions.map((institution) => ({ value: String(institution.id), label: institution.name })),
    [institutions],
  );

  const getErrorMessage = (error: unknown) => {
    if (typeof error === "object" && error && "response" in error) {
      const response = (error as { response?: { data?: { error?: unknown; detail?: unknown; message?: unknown } } }).response;
      return formatErrorMessage(response?.data?.detail) || formatErrorMessage(response?.data?.error) || formatErrorMessage(response?.data?.message) || t("savingPockets.loadError");
    }
    return t("savingPockets.loadError");
  };

  const handleSubmit = async () => {
    setSubmitError(null);

    try {
      if (mode === "institution") {
        await createInstitution.mutateAsync({
          name: institutionForm.name.trim(),
          description: institutionForm.description.trim() || undefined,
          active: institutionForm.active,
        });
      } else {
        const freezeTime = parseFreezeTime(pocketForm.freeze_time);
        await createPocket.mutateAsync({
          saving_institution_id: Number(pocketForm.saving_institution_id),
          name: pocketForm.name.trim(),
          amount: Number(pocketForm.amount || 0),
          anual_rate: Number(pocketForm.anual_rate || 0),
          freeze_time: freezeTime,
          freeze_date: freezeTime ? pocketForm.starting_date : null,
          description: pocketForm.description.trim() || undefined,
          active: pocketForm.active,
        });
      }
      onClose();
    } catch (error) {
      setSubmitError(getErrorMessage(error));
    }
  };

  const canSubmitInstitution = institutionForm.name.trim().length > 0;
  const canSubmitPocket = pocketForm.name.trim().length > 0 && Number(pocketForm.saving_institution_id) > 0;

  return (
    <ModalShell
      isOpen={isOpen}
      onClose={onClose}
      title={t("savingPockets.modalTitle")}
      subtitle={t("savingPockets.modalSubtitle")}
      labelledBy="new-savings-pocket-modal-title"
      mobileSheet
      variant="creation"
      footer={
        <ModalActions
          cancelLabel={t("common.actions.cancel")}
          submitLabel={mode === "institution" ? t("savingPockets.createInstitution") : t("savingPockets.createPocket")}
          submittingLabel={t("common.states.creating")}
          onCancel={onClose}
          onSubmit={() => {
            void handleSubmit();
          }}
          isSubmitting={isSubmitting}
          isSubmitDisabled={mode === "institution" ? !canSubmitInstitution : !canSubmitPocket}
        />
      }
    >
      <div className="app-creation-form space-y-3 md:space-y-5">
        <SegmentedControl
          value={mode}
          onChange={setMode}
          ariaLabel={t("savingPockets.creationType")}
          options={[
            { id: "institution", label: t("savingPockets.addInstitution") },
            { id: "pocket", label: t("savingPockets.addPocket") },
          ]}
          containerClassName="grid w-full max-w-full grid-cols-2 gap-1 rounded-full border border-[var(--ds-line)] bg-[var(--ds-surface)] p-1 md:inline-grid md:w-auto"
          buttonClassName="min-h-11 min-w-0 rounded-full px-3 py-2 text-sm font-medium md:px-4"
          activeButtonClassName="bg-[var(--ds-card-sun)] text-[var(--ds-on-card)]"
          inactiveButtonClassName="text-[var(--ds-ink)] hover:bg-[var(--ds-ground)]"
        />

        {submitError ? <MessageBanner tone="error">{submitError}</MessageBanner> : null}

        {mode === "institution" ? (
          <div className="app-creation-form-grid grid grid-cols-2 gap-2">
            <Field id="saving-pocket-institution-name" label={t("savingPockets.institutionName")} value={institutionForm.name} onChange={(value) => setInstitutionForm((current) => ({ ...current, name: value }))} placeholder={t("savingPockets.institutionPlaceholder")} required />
            <ActiveField label={t("savingPockets.activeLabel")} checked={institutionForm.active} onChange={(checked) => setInstitutionForm((current) => ({ ...current, active: checked }))} />
            <div className="app-creation-form-full col-span-2">
              <TextAreaField id="saving-pocket-institution-description" label={t("savingPockets.description")} value={institutionForm.description} onChange={(value) => setInstitutionForm((current) => ({ ...current, description: value }))} placeholder={t("savingPockets.descriptionPlaceholder")} />
            </div>
          </div>
        ) : (
          <div className="app-creation-form-grid grid grid-cols-2 gap-2">
            <SelectField id="saving-pocket-institution" label={t("savingPockets.institution")} value={pocketForm.saving_institution_id} onChange={(value) => setPocketForm((current) => ({ ...current, saving_institution_id: value }))} options={institutionOptions} required />
            <Field id="saving-pocket-name" label={t("savingPockets.pocketName")} value={pocketForm.name} onChange={(value) => setPocketForm((current) => ({ ...current, name: value }))} placeholder={t("savingPockets.pocketPlaceholder")} required />
            <Field id="saving-pocket-amount" label={t("savingPockets.amount")} type="number" value={pocketForm.amount} onChange={(value) => setPocketForm((current) => ({ ...current, amount: value }))} placeholder="25000" />
            <Field id="saving-pocket-annual-rate" label={t("savingPockets.annualRate")} type="number" value={pocketForm.anual_rate} onChange={(value) => setPocketForm((current) => ({ ...current, anual_rate: value }))} placeholder="12" />
            <FreezeTimeField
              id="saving-pocket-freeze-time"
              label={t("savingPockets.freezeTime")}
              layout="grid"
              preset={freezePreset}
              value={pocketForm.freeze_time}
              onPresetChange={(preset) => {
                setFreezePreset(preset);
                setPocketForm((current) => ({
                  ...current,
                  freeze_time: preset === "none" ? "" : preset === "other" ? current.freeze_time : preset,
                }));
              }}
              onCustomValueChange={(value) => setPocketForm((current) => ({ ...current, freeze_time: value }))}
            />
            <Field id="saving-pocket-starting-date" label={t("savingPockets.startingDate")} type="date" value={pocketForm.starting_date} onChange={(value) => setPocketForm((current) => ({ ...current, starting_date: value }))} placeholder="" />
            <ReadOnlyField label={t("savingPockets.unfreezeDate")} value={formatComputedUnfreezeDate(pocketForm.starting_date, pocketForm.freeze_time) || t("savingPockets.none")} />
            <div className="app-creation-form-full col-span-2">
              <TextAreaField id="saving-pocket-description" label={t("savingPockets.description")} value={pocketForm.description} onChange={(value) => setPocketForm((current) => ({ ...current, description: value }))} placeholder={t("savingPockets.descriptionPlaceholder")} />
            </div>
            <ActiveField className="app-creation-form-full col-span-2" label={t("savingPockets.activeLabel")} checked={pocketForm.active} onChange={(checked) => setPocketForm((current) => ({ ...current, active: checked }))} />
          </div>
        )}
      </div>
    </ModalShell>
  );
};

function parseFreezeTime(freezeTime: string) {
  const days = Number(freezeTime);
  if (!Number.isFinite(days) || days <= 0) return null;
  return Math.trunc(days);
}

function formatErrorMessage(value: unknown) {
  if (typeof value === "string") return value;
  if (Array.isArray(value)) {
    return value
      .map((item) => {
        if (typeof item === "string") return item;
        if (typeof item === "object" && item !== null && "msg" in item) return String((item as { msg: unknown }).msg);
        return "";
      })
      .filter(Boolean)
      .join(". ");
  }
  return "";
}

function getFreezePreset(freezeTime: string): FreezePreset {
  if (!freezeTime) return "none";
  if (FREEZE_PRESET_OPTIONS.includes(freezeTime as FreezePreset)) return freezeTime as FreezePreset;
  return "other";
}

function getTodayInputValue() {
  const date = new Date();
  const timezoneOffset = date.getTimezoneOffset() * 60_000;
  return new Date(date.getTime() - timezoneOffset).toISOString().slice(0, 10);
}

function formatComputedUnfreezeDate(startingDate: string, freezeTime: string) {
  const days = parseFreezeTime(freezeTime);
  if (!startingDate || !days) return "";
  const date = new Date(`${startingDate}T00:00:00`);
  if (Number.isNaN(date.getTime())) return "";
  date.setDate(date.getDate() + days);
  return date.toLocaleDateString();
}

// Rendered through FormField so it picks up the same compact tile, label clamp and
// row alignment as every other creation field.
function ReadOnlyField({ label, value }: { label: string; value: string }) {
  return (
    <FormField label={label} htmlFor="saving-pocket-unfreeze-date">
      <div id="saving-pocket-unfreeze-date" className={[fieldControlClass, "bg-[var(--app-surface-soft)] text-[var(--app-muted-text)]"].join(" ")}>
        {value}
      </div>
    </FormField>
  );
}

function ActiveField({
  label,
  checked,
  onChange,
  className = "",
}: {
  label: string;
  checked: boolean;
  onChange: (checked: boolean) => void;
  className?: string;
}) {
  return (
    <label
      className={`flex min-h-11 cursor-pointer items-center gap-3 self-end rounded-xl border border-transparent bg-[color:color-mix(in_srgb,var(--app-surface-soft)_88%,var(--app-surface)_12%)] px-3 py-2 text-sm font-medium text-[var(--app-text)] md:border-[var(--app-border)] md:bg-transparent md:px-4 md:py-3 ${className}`}
    >
      <input
        type="checkbox"
        className="h-4 w-4 shrink-0 accent-[var(--app-primary)]"
        checked={checked}
        onChange={(event) => onChange(event.target.checked)}
      />
      <span className="min-w-0 truncate">{label}</span>
    </label>
  );
}

function TextAreaField({ id, label, value, onChange, placeholder }: { id: string; label: string; value: string; onChange: (value: string) => void; placeholder: string }) {
  return (
    <FormField label={label} htmlFor={id}>
      <TextArea id={id} value={value} onChange={(event) => onChange(event.target.value)} placeholder={placeholder} rows={4} />
    </FormField>
  );
}
